/*
 * File: _error.js
 * Project: meki
 * File Created: Saturday, 16th January 2021 10:04:31 pm
 * Author: Gabriel Ulloa (gabriel@inventures.cl)
 * -----
 * Last Modified: Tuesday, 24th January 2023 5:24:26 pm
 * Modified By: Gabriel Ulloa (gabriel@inventures.cl)
 * -----
 * Copyright 2019 - 2021 Incrementa Ventures SpA. ALL RIGHTS RESERVED
 * Terms and conditions defined in license.txt
 * -----
 * Inventures - www.inventures.cl
 */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
import NextErrorComponent from 'next/error';

import { ErrorScreen } from '@screens/errors';
import { Console } from '@utils';
import { getSentry } from '@services/sentry';
import { useOpenReplayTracking } from '@services/openreplay';
import { useSegment } from '@hooks/useSegment';

const MyError = ({ hasGetInitialPropsRun, err }) => {
  const { trackIssue } = useOpenReplayTracking();
  const { track } = useSegment();
  Console.error({ msg: 'FATAL ERROR', hasGetInitialPropsRun, err });
  void trackIssue('Error screen', { err, hasGetInitialPropsRun });
  void track('Error generico');
  if (!hasGetInitialPropsRun && err) {
    // getInitialProps is not called in case of
    // https://github.com/vercel/next.js/issues/8592. As a workaround, we pass
    // err via _app.js so it can be captured
    void getSentry().then((Sentry) => {
      Sentry.captureException(err);
    });
    // Flushing is not required in this case as it only happens on the client
  }

  return <ErrorScreen />;
};

MyError.getInitialProps = async ({ res, err, asPath }) => {
  Console.error({ msg: 'FATAL ERROR, getInitialProps', err, asPath });
  const errorInitialProps = await NextErrorComponent.getInitialProps({
    res,
    err,
  });

  // Workaround for https://github.com/vercel/next.js/issues/8592, mark when
  // getInitialProps has run
  errorInitialProps.hasGetInitialPropsRun = true;

  // Running on the server, the response object (`res`) is available.
  //
  // Next.js will pass an err on the server if a page's data fetching methods
  // threw or returned a Promise that rejected
  //
  // Running on the client (browser), Next.js will provide an err if:
  //
  //  - a page's `getInitialProps` threw or returned a Promise that rejected
  //  - an exception was thrown somewhere in the React lifecycle (render,
  //    componentDidMount, etc) that was caught by Next.js's React Error
  //    Boundary. Read more about what types of exceptions are caught by Error
  //    Boundaries: https://reactjs.org/docs/error-boundaries.html

  const Sentry = await getSentry();
  if (err) {
    Sentry.captureException(err);

    // Flushing before returning is necessary if deploying to Vercel, see
    // https://vercel.com/docs/platform/limits#streaming-responses
    await Sentry.flush(2000);

    return errorInitialProps;
  }

  // If this point is reached, getInitialProps was called without any
  // information about what the error might be. This is unexpected and may
  // indicate a bug introduced in Next.js, so record it in Sentry
  Sentry.captureException(
    new Error(`_error.js getInitialProps missing data at path: ${asPath}`),
  );
  await Sentry.flush(2000);

  return errorInitialProps;
};

export default MyError;
